// sessionStorage = data is cleared when the page session ends.
// localStorage = data is bounded to the site, it does not expires
import * as DateFnsNs from 'date-fns'
import { jwtDecode } from 'jwt-decode'
import { Logger, NOOP_VOID } from 'src/3rds/cube'

export type AccessTokenAboutToExpire = (sender: AppStorage, token: string) => void

type VoidListenerType = () => void

export type UserRole = 'admin' | 'manager' | 'user' | 'viewer'

export type AuthUser = {
    id: number
    name: string
    email: string
    role: UserRole
    position?: string
}

export type AuthPermissions = {
    userManager: boolean
    compliant: boolean
    canExport: boolean
}

type Authorization = {
    token: string
    user?: AuthUser
    permissions?: AuthPermissions
    // Legacy fields (deprecated)
    rdStation?: object
    theMap?: object
    theActing?: { print: boolean }
    theConsumerMarket?: {
        ia: boolean
        companies: boolean
        partners: boolean
        doctors: boolean
        relationships: boolean
    }
    theConnection?: {
        companies: boolean
        partners: boolean
        doctors: boolean
        relationships: boolean
    }
    userManager?: object
    position?: string
}

const LOG = Logger.get('AppStorage')

type StorageProperty<T> = {
    get: () => T
    set: (value: unknown, rememberMe?: boolean) => void
    clear: () => void
}

const EXPIRATION_DATE_IN_MILLIS = Date.now()

export class AppStorage {
    private static readonly INSTANCE = new AppStorage()

    public static singleton = () => AppStorage.INSTANCE

    private __eventIdGen = 0

    private __tokenAbouteToExpireMap = new Map<number, AccessTokenAboutToExpire>()
    private __tokenAboutToExpireTimeHandler: VoidListenerType = NOOP_VOID

    private __rememberMeProp = newLocalStorageBoolProperty('3ae9b5f1')
    private __accesssTokenProp = newStorageStringProperty('a9c07324')
    private __userManagerEnabledProp = newStorageBoolProperty('9834a9ax')
    private __userNameProp = newStorageStringProperty('J9VCpx')
    private __userPositionProp = newStorageStringProperty('J9CPpxC')

    // New user properties
    private __userIdProp = newStorageStringProperty('usr_id')
    private __userEmailProp = newStorageStringProperty('usr_email')
    private __userRoleProp = newStorageStringProperty('usr_role')
    private __compliantEnabledProp = newStorageBoolProperty('perm_compliant')
    private __canExportEnabledProp = newStorageBoolProperty('perm_export')
    private __authenticatedProp = newStorageBoolProperty('usr_authenticated')

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private constructor() {}

    get rememberMe() {
        return this.__rememberMeProp.get()
    }

    get userManagerEnabled() {
        return this.__userManagerEnabledProp.get()
    }

    get userPosition(): string {
        return this.__userPositionProp.get()
    }

    get userId(): number | null {
        const id = this.__userIdProp.get()
        return id ? parseInt(id, 10) : null
    }

    get userEmail(): string {
        return this.__userEmailProp.get()
    }

    get userRole(): UserRole | null {
        return this.__userRoleProp.get() as UserRole | null
    }

    get compliantEnabled(): boolean {
        return this.__compliantEnabledProp.get()
    }

    get canExportEnabled(): boolean {
        return this.__canExportEnabledProp.get()
    }

    get isAuthenticated(): boolean {
        return this.__authenticatedProp.get() && this.accessTokenExpiration >= Date.now()
    }

    setAuthenticated(value: boolean, rememberMe: boolean): void {
        this.__authenticatedProp.set(value, rememberMe)
    }

    get currentUser(): AuthUser | null {
        const id = this.userId
        const name = this.__userNameProp.get()
        const email = this.userEmail
        const role = this.userRole

        if (!id || !name || !role) return null

        return {
            id,
            name,
            email,
            role,
            position: this.userPosition || undefined
        }
    }

    get accessTokenExpiration() {
        try {
            const accessToken = this.accessToken
            if (!accessToken) {
                return EXPIRATION_DATE_IN_MILLIS
            }

            const info = decodeAccessToken(accessToken)

            return info.exp * 1000
        } catch (err) {
            LOG.error('[accessTokenExpirationDate]', err)
            return EXPIRATION_DATE_IN_MILLIS
        }
    }

    get accessToken() {
        return this.__accesssTokenProp.get()
    }

    clearAccessToken() {
        this.__rememberMeProp.clear()
        this.__accesssTokenProp.clear()
        this.__userManagerEnabledProp.clear()
        this.__userPositionProp.clear()
        // New fields
        this.__userIdProp.clear()
        this.__userEmailProp.clear()
        this.__userRoleProp.clear()
        this.__compliantEnabledProp.clear()
        this.__canExportEnabledProp.clear()
        this.__authenticatedProp.clear()

        this.__tokenAboutToExpireTimeHandler()
        this.__tokenAboutToExpireTimeHandler = NOOP_VOID
    }

    get userName(): string {
        return this.__userNameProp.get()
    }

    setUserName(username: string, rememberMe: boolean) {
        this.__userNameProp.set(username, rememberMe)
    }
    
    setAuthorization(value: Omit<Authorization, 'token'>, rememberMe: boolean) {
        this.__rememberMeProp.set(rememberMe, false)
        
        // New format (user + permissions)
        if (value.user) {
            this.__userIdProp.set(String(value.user.id), rememberMe)
            this.__userNameProp.set(value.user.name, rememberMe)
            this.__userEmailProp.set(value.user.email, rememberMe)
            this.__userRoleProp.set(value.user.role, rememberMe)
            this.__userPositionProp.set(value.user.position, rememberMe)
        }
        
        if (value.permissions) {
            this.__userManagerEnabledProp.set(value.permissions.userManager, rememberMe)
            this.__compliantEnabledProp.set(value.permissions.compliant, rememberMe)
            this.__canExportEnabledProp.set(value.permissions.canExport, rememberMe)
        }

        // Legacy userManager/position if new format not present
        if (!value.permissions && value.userManager !== undefined) {
            this.__userManagerEnabledProp.set(value.userManager, rememberMe)
        }
        if (!value.user && value.position !== undefined) {
            this.__userPositionProp.set(value.position, rememberMe)
        }

        const token = this.__accesssTokenProp.get()
        this.launchTokenExpirationTimeout(token)
    }

    setAccessToken(value: Authorization, rememberMe: boolean) {
        this.clearAccessToken()
        if (!value || !value.token) {
            return
        }
        this.__accesssTokenProp.set(value.token, rememberMe)
        this.setAuthorization(value, rememberMe)
    }

    onAccessTokenAboutToExpire(listener: AccessTokenAboutToExpire) {
        const eventId = this.__eventIdGen++
        this.__tokenAbouteToExpireMap.set(eventId, listener)

        const accessToken = this.accessToken
        if (accessToken) {
            this.launchTokenExpirationTimeout(accessToken)
        } else {
            this.__tokenAboutToExpireTimeHandler()
            this.__tokenAboutToExpireTimeHandler = NOOP_VOID
        }

        return () => {
            this.__tokenAbouteToExpireMap.delete(eventId)
        }
    }

    private launchTokenExpirationTimeout(accessToken: string) {
        this.__tokenAboutToExpireTimeHandler()
        this.__tokenAboutToExpireTimeHandler = NOOP_VOID

        const info = decodeAccessToken(accessToken)
        const millisToExpire = DateFnsNs.differenceInMilliseconds(info.exp * 1000, Date.now())
        if (millisToExpire > 0) {
            const refreshToken = () => {
                for (const listener of this.__tokenAbouteToExpireMap.values()) {
                    try {
                        listener(this, accessToken)
                    } catch (err) {
                        LOG.error(`Processing listner ${listener}`, err)
                    }
                }
            }

            const oneHourInMillis = DateFnsNs.minutesToMilliseconds(60)

            if (millisToExpire < oneHourInMillis) {
                // Do it now
                refreshToken()
            }

            // And, refresh at fix rate of one hour
            this.__tokenAboutToExpireTimeHandler = scheduleAtFixRate(refreshToken, oneHourInMillis)
        }
    }
}

function decodeAccessToken(accessToken: string) {
    return jwtDecode(accessToken) as {
        username: string
        sub: number
        iat: number
        exp: number
    }
}

function scheduleAtFixRate(listner: VoidListenerType, interval: number) {
    const ctx = {
        handler: undefined as NodeJS.Timeout | undefined,
        stoped: false
    }

    const relauchListener: VoidListenerType = () => {
        if (ctx.stoped) {
            return
        }

        try {
            ctx.handler = undefined
            listner()
        } catch (err) {
            LOG.error('[scheduleAtFixRate]', err)
        } finally {
            ctx.handler = setTimeout(relauchListener, interval)
        }
    }

    ctx.handler = setTimeout(relauchListener, interval)

    return () => {
        ctx.stoped = true
        if (ctx.handler) {
            clearTimeout(ctx.handler)
            ctx.handler = undefined
        }
    }
}

function newLocalStorageBoolProperty(uid: string): StorageProperty<boolean> {
    return {
        set: (value: unknown) => {
            localStorage.setItem(uid, value ? 'true' : 'false')
        },
        get: () => {
            return localStorage.getItem(uid) === 'true'
        },
        clear: () => {
            localStorage.removeItem(uid)
        }
    }
}

function newStorageBoolProperty(uid: string): StorageProperty<boolean> {
    return {
        set: (value: unknown, rememberMe?: boolean) => {
            if (rememberMe) {
                localStorage.setItem(uid, value ? 'true' : 'false')
                sessionStorage.removeItem(uid)
            } else {
                sessionStorage.setItem(uid, value ? 'true' : 'false')
                localStorage.removeItem(uid)
            }
        },
        get: () => {
            return localStorage.getItem(uid) === 'true' || sessionStorage.getItem(uid) === 'true'
        },
        clear: () => {
            localStorage.removeItem(uid)
            sessionStorage.removeItem(uid)
        }
    }
}

function newStorageStringProperty(uid: string): StorageProperty<string> {
    return {
        set: (value: unknown, rememberMe?: boolean) => {
            if (rememberMe) {
                if (value) {
                    localStorage.setItem(uid, `${value}`)
                } else {
                    localStorage.removeItem(uid)
                }

                sessionStorage.removeItem(uid)
            } else {
                if (value) {
                    sessionStorage.setItem(uid, `${value}`)
                } else {
                    sessionStorage.removeItem(uid)
                }
                localStorage.removeItem(uid)
            }
        },
        get: () => {
            return localStorage.getItem(uid) || sessionStorage.getItem(uid) || ''
        },
        clear: () => {
            localStorage.removeItem(uid)
            sessionStorage.removeItem(uid)
        }
    }
}

export default AppStorage
